import React from "react";
import { createRoot } from "react-dom/client";
import { Pie, Column, DualAxes } from "@ant-design/plots";
import { Renderer as SVGRenderer } from "@antv/g-svg";
import "svg2pdf.js"; // side-effect: agrega doc.svg(...) a jsPDF
import { formatPercentage } from "../../../utils/numberFormat";

// ─────────────────────────────────────────────────────────────────────────
// Renderiza gráficos de G2 (@ant-design/plots) como SVG vectorial fuera de
// pantalla y los dibuja en un PDF (jsPDF + svg2pdf.js) junto con su título y
// leyenda de pills. Reemplaza la captura por html2canvas de las dos filas de
// gráficos en GeneralDashboard.js — validado con un prototipo antes de migrar.
// ─────────────────────────────────────────────────────────────────────────

async function mountOffscreen(Component, config, width, height) {
  const container = document.createElement("div");
  container.style.cssText = [
    "position:absolute",
    "left:-9999px",
    "top:0",
    `width:${width}px`,
    `height:${height}px`,
    "background:#fff",
  ].join(";");
  document.body.appendChild(container);
  const root = createRoot(container);

  await new Promise((resolve) => {
    root.render(React.createElement(Component, { ...config, width, height, onReady: () => resolve() }));
  });
  // onReady dispara justo después de llamar a chart.render(), pero G2 sigue
  // reacomodando cosas de forma asíncrona después de eso — en particular,
  // una segunda pasada que mide el texto real de las etiquetas de eje y
  // decide cuáles ocultar/reacomodar para que no se superpongan. Un delay
  // fijo corto (350ms) alcanzaba casi siempre, pero bajo carga (varios
  // gráficos renderizando a la vez) a veces esa segunda pasada tardaba más,
  // y se capturaba el SVG a mitad de camino — etiquetas superpuestas,
  // intermitente, se arreglaba reintentando la descarga. En vez de apostar
  // a un número fijo, se espera hasta que el SVG deje de cambiar entre dos
  // frames seguidos (con un tope máximo para no colgarse si nunca se
  // estabiliza).
  let lastSnapshot = null;
  let stableFrames = 0;
  const settleStart = Date.now();
  while (stableFrames < 2 && Date.now() - settleStart < 2000) {
    await new Promise((r) => requestAnimationFrame(r));
    const snapshot = container.querySelector("svg")?.outerHTML || "";
    if (snapshot === lastSnapshot) {
      stableFrames++;
    } else {
      stableFrames = 0;
      lastSnapshot = snapshot;
    }
  }

  const svgEl = container.querySelector("svg");
  const cleanup = () => { root.unmount(); document.body.removeChild(container); };

  // Por default el <svg> preserva su proporción al incrustarlo (svg2pdf.js
  // respeta el preserveAspectRatio="xMidYMid meet" implícito) — lo centra
  // dentro del rectángulo destino y deja franjas en blanco a los costados
  // en vez de ocupar el ancho/alto pedidos. drawChartCard igual calcula a
  // mano un rectángulo destino con la proporción real (no le afecta esto),
  // pero drawChartSection estira a propósito para llenar la sección; "none"
  // habilita ese estirado no uniforme.
  if (svgEl) svgEl.setAttribute("preserveAspectRatio", "none");

  // Algunos formatters de eje (ej. formatWhole) usan un espacio fino
  // U+202F como separador de miles — no existe en la codificación WinAnsi
  // de las fuentes estándar de PDF, y svg2pdf.js lo corrompe igual que le
  // pasaba a doc.text() (texto nativo, sanitizado aparte donde se dibuja).
  // Se limpia acá, a nivel de nodo <text> del SVG, para que cualquier
  // gráfico que use un formatter así quede cubierto sin tener que
  // acordarse de sanitizar en cada callsite.
  // Sub\u00EDndices Unicode (\u2080-\u2089, ej. "CO\u2082") tampoco existen en WinAnsi \u2014 misma
  // corrupci\u00F3n que el espacio fino, se ven bien en pantalla pero rompen el
  // texto del SVG al incrustarlo (y todo lo que sigue en ese nodo <text>,
  // que queda desplazado). Se pasan a d\u00EDgitos ASCII normales.
  if (svgEl) {
    svgEl.querySelectorAll("text").forEach((node) => {
      if (node.textContent) {
        node.textContent = node.textContent
          .replace(/[\u202F\u2009\u2007\u2060\u00A0]/g, " ")
          .replace(/[\u2080-\u2089]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x2080 + 0x30));
      }
    });
  }

  // El <svg> que arma G2 declara width/height/viewBox = el tamaño del
  // contenedor que le pedimos, pero para gráficos de barras con varias
  // categorías el layout interno a veces calcula una banda cuyo borde
  // termina un poco más allá de ese viewBox declarado (la última barra
  // recortada al exportar). En vez de perseguir el motivo exacto ajustando
  // el ancho del contenedor a mano, se mide el bounding box REAL de lo que
  // se dibujó y se agranda el viewBox para que contenga todo el contenido
  // — así nunca se recorta, sea cual sea la causa del desborde.
  let naturalW = width;
  let naturalH = height;
  if (svgEl) {
    const bbox = svgEl.getBBox();
    const PAD = 2;
    const x = Math.min(0, bbox.x) - PAD;
    const y = Math.min(0, bbox.y) - PAD;
    naturalW = Math.max(width, bbox.x + bbox.width) - x + PAD;
    naturalH = Math.max(height, bbox.y + bbox.height) - y + PAD;
    svgEl.setAttribute("viewBox", `${x} ${y} ${naturalW} ${naturalH}`);
    svgEl.setAttribute("width", naturalW);
    svgEl.setAttribute("height", naturalH);
  }

  return { svgEl, cleanup, naturalW, naturalH };
}

const CHART_PX = 220; // tamaño (cuadrado) del contenedor off-screen para renderizar la dona
// Las barras necesitan más ancho que alto: con un contenedor cuadrado
// angosto, G2 no tiene lugar para calcular las bandas de 4-5 categorías más
// el margen del eje Y, y la última barra queda con su ancho calculado más
// allá del viewBox del SVG — el propio SVG la recorta al exportarla.
const BAR_CHART_W = 340;
const BAR_CHART_H = 210;
// Los DualAxes de Energía tienen 12 meses × hasta 3 años (36 categorías) en
// el eje X — con el mismo ancho angosto que las barras de 3-5 categorías,
// G2 no tiene lugar para las etiquetas de mes y termina ocultando/apretando
// la mayoría. drawChartSection igual reescala el SVG resultante al ancho
// real de la página, así que renderizarlo bien ancho acá (más cerca del
// ancho final en el PDF) no cambia el tamaño final, solo le da a G2 el
// espacio que necesita para no comprimir las etiquetas.
// Si se renderizan mucho más chicos que su tamaño final en el PDF, el
// estirado final agranda el texto de más (es parte del SVG, se escala junto
// con todo lo demás) — se los renderiza cerca de ese tamaño (~la mitad de
// una hoja, comparten página) para que el texto quede proporcionado.
const DUAL_AXES_W = 780;
const DUAL_AXES_H = 300;

// `showPercentLabels`: opcional, apagado por default para no cambiar el
// aspecto de las donas ya migradas (su versión en pantalla no muestra
// porcentaje sobre el gráfico). Algunas donas (ej. "Métodos de disposición"
// de Residuos) sí lo muestran en pantalla (label spider con formatPercentage)
// — se replica ese mismo estilo acá cuando se pide.
export async function renderDonutForPdf(data, colorMap, { showPercentLabels = false } = {}) {
  const allData = (data || []).filter((d) => d.value > 0);
  if (allData.length === 0) return { isEmpty: true };

  // `type` es la clave que matchea contra colorMap (fija, sin traducir);
  // `label`, si el caller lo pasa, es el texto a mostrar (ej. dashboards con
  // i18n) — por defecto es el mismo `type`, sin cambiar a los callers ya
  // existentes.
  const legendItems = allData.map((d) => ({ label: d.label ?? d.type, color: colorMap[d.type] || "#ccc" }));

  // Una sola categoría con 100% del total es un caso degenerado para el
  // arco SVG (ángulo inicial = ángulo final, círculo completo): el
  // renderer SVG de G2 lo dibuja como una espiral mal formada en vez de un
  // anillo cerrado. En vez de depender de G2 para ese caso puntual, se
  // dibuja el anillo directo con primitivas de jsPDF (dos círculos llenos)
  // en drawChartCard — evita el bug de raíz sin tocar el resto de casos.
  if (allData.length === 1) {
    return {
      svgEl: null,
      cleanup: null,
      legendItems,
      singleSliceColor: colorMap[allData[0].type] || "#ccc",
      isEmpty: false,
    };
  }

  const total = allData.reduce((acc, d) => acc + d.value, 0);
  const config = {
    data: allData,
    angleField: "value",
    colorField: "type",
    scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
    radius: 1,
    innerRadius: 0.6,
    label: showPercentLabels
      ? {
          text: (datum) => {
            const percent = total > 0 ? (datum?.value / total) * 100 : 0;
            return percent > 0 ? formatPercentage(percent) : "";
          },
          position: "spider",
          style: { fontSize: 8, fontWeight: 600 },
        }
      : false,
    legend: false,
    animate: false,
    renderer: new SVGRenderer(),
  };
  const { svgEl, cleanup, naturalW, naturalH } = await mountOffscreen(Pie, config, CHART_PX, CHART_PX);
  return { svgEl, cleanup, legendItems, naturalW, naturalH, isEmpty: false };
}

// `yFormatter`: opcional (ej. formatWhole/formatMagnitudeAdaptive de
// numberFormat.js, para AJUSTES DE MAGNITUDES NUMÉRICAS) — sin uso por
// default para no cambiar el aspecto de los dashboards que ya llaman a esta
// función compartida sin haber pedido ese ajuste. El separador de miles fino
// que usan esos formatters se sanea solo a espacio normal en mountOffscreen
// (no rompe el SVG, a diferencia del texto nativo de jsPDF).
export async function renderBarForPdf(data, yLabel, palette, { yFormatter } = {}) {
  const rows = data || [];
  if (rows.length === 0) return { isEmpty: true };

  const colorMap = {};
  rows.forEach((d, i) => { colorMap[d.year] = palette[i % palette.length] || "#ccc"; });
  const config = {
    data: rows,
    xField: "year",
    yField: "value",
    colorField: "year",
    scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
    // Sin radius: las esquinas redondeadas se dibujan como arcos chicos, y
    // el mismo bug del renderer SVG de G2 que produce la espiral en la dona
    // (ver renderDonutForPdf) deja un artefacto — una franja más clara — en
    // el borde de la barra más alta de cada serie. Barras rectas lo evitan.
    style: { maxWidth: 60 },
    legend: false,
    animate: false,
    axis: {
      x: { title: false, labelFontSize: 9 },
      // G2 hace String(title) para dibujarlo (ver titleContent() en
      // @antv/g2) — un objeto {text, style} se convierte literalmente en el
      // texto "[object Object]". El título tiene que ser un string plano.
      y: { title: yLabel || false, labelFontSize: 9, ...(yFormatter ? { labelFormatter: yFormatter } : {}) },
    },
    renderer: new SVGRenderer(),
  };
  const { svgEl, cleanup, naturalW, naturalH } = await mountOffscreen(Column, config, BAR_CHART_W, BAR_CHART_H);
  const legendItems = rows.map((d) => ({ label: String(d.year), color: colorMap[d.year] }));
  return { svgEl, cleanup, legendItems, naturalW, naturalH, isEmpty: false };
}

// Barras agrupadas por TIPO (no por año) — varias series posibles por
// categoría del eje X (ej. "Efluente Industrial", "Consumo Agua
// Producción"), coloreadas desde un mapa fijo tipo→color (como las donas),
// a diferencia de renderBarForPdf que colorea por año con una paleta por
// índice.
export async function renderGroupedBarForPdf(data, colorMap, yLabel, { yFormatter } = {}) {
  const rows = (data || []).filter((d) => d.value > 0);
  if (rows.length === 0) return { isEmpty: true };

  const types = [...new Set(rows.map((d) => d.type))];
  const config = {
    data: rows,
    xField: "year",
    yField: "value",
    colorField: "type",
    group: true,
    scale: { color: { domain: types, range: types.map((t) => colorMap[t] || "#ccc") } },
    style: { maxWidth: 40 },
    legend: false,
    animate: false,
    axis: {
      x: { title: false, labelFontSize: 9 },
      y: { title: yLabel || false, labelFontSize: 9, ...(yFormatter ? { labelFormatter: yFormatter } : {}) },
    },
    renderer: new SVGRenderer(),
  };
  const { svgEl, cleanup, naturalW, naturalH } = await mountOffscreen(Column, config, BAR_CHART_W, BAR_CHART_H);
  const legendItems = types.map((type) => ({ label: type, color: colorMap[type] || "#ccc" }));
  return { svgEl, cleanup, legendItems, naturalW, naturalH, isEmpty: false };
}

// Barras agrupadas por AÑO dentro de cada categoría de TIPO (eje X) —
// inverso de renderGroupedBarForPdf: acá el eje X es el tipo (hasta ~13
// categorías) y el color/leyenda es por año con paleta por índice (como
// renderBarForPdf), no un mapa fijo tipo→color.
export async function renderTypeGroupedBarForPdf(data, yLabel, palette, { yFormatter } = {}) {
  const rows = (data || []).filter((d) => d.value > 0);
  if (rows.length === 0) return { isEmpty: true };

  const years = [...new Set(rows.map((d) => d.year))];
  const colorMap = {};
  years.forEach((y, i) => { colorMap[y] = palette[i % palette.length] || "#ccc"; });

  const config = {
    data: rows,
    xField: "type",
    yField: "value",
    colorField: "year",
    group: true,
    scale: { color: { domain: years, range: years.map((y) => colorMap[y]) } },
    style: { maxWidth: 40 },
    legend: false,
    animate: false,
    axis: {
      x: { title: false, labelFontSize: 8 },
      y: { title: yLabel || false, labelFontSize: 9, ...(yFormatter ? { labelFormatter: yFormatter } : {}) },
    },
    renderer: new SVGRenderer(),
  };
  // Mount ancho (como los DualAxes) en vez de BAR_CHART_W: hasta 13
  // categorías en el eje X necesitan espacio para no comprimir etiquetas, y
  // se dibuja con drawChartSection (ancho completo de página), no
  // drawChartCard.
  const { svgEl, cleanup, naturalW, naturalH } = await mountOffscreen(Column, config, DUAL_AXES_W, DUAL_AXES_H);
  const legendItems = years.map((year) => ({ label: String(year), color: colorMap[year] }));
  return { svgEl, cleanup, legendItems, naturalW, naturalH, isEmpty: false };
}

// Gráfico compuesto (ej. barra+línea) — el caller arma `children` ya en el
// mismo formato que espera G2, porque su composición (ejes ocultos por
// capa, estilos de línea punteada, etc.) es específica de cada caso de uso
// y no vale la pena generalizar acá.
export async function renderDualAxesForPdf(children, { xField, scale, axis } = {}) {
  const hasData = (children || []).some((c) => c.data?.length);
  if (!hasData) return { isEmpty: true };

  const config = {
    children,
    xField,
    scale,
    axis,
    legend: false,
    animate: false,
    renderer: new SVGRenderer(),
  };
  const { svgEl, cleanup, naturalW, naturalH } = await mountOffscreen(DualAxes, config, DUAL_AXES_W, DUAL_AXES_H);
  return { svgEl, cleanup, naturalW, naturalH, isEmpty: false };
}

// ── Dibujo nativo (jsPDF) ───────────────────────────────────────────────

function hexToRgb(hex) {
  const m = (hex || "").replace("#", "").match(/.{1,2}/g);
  if (!m || m.length < 3) return [180, 180, 180];
  return m.slice(0, 3).map((h) => parseInt(h, 16));
}

const PILL_H = 13;
const PILL_GAP = 6;

// Agrupa las pills en líneas (wrap) respetando maxWidth — factorizado aparte
// de drawLegendPills para poder MEDIR cuántas líneas van a hacer falta
// antes de dibujar el gráfico (ver measureLegendHeight): antes
// drawChartCard reservaba una altura fija de leyenda (26pt, una sola línea)
// sin importar cuántos ítems tenía, y con 4+ ítems de texto largo (ej.
// "Consumo Agua Producción") que envolvían a una segunda línea, esa segunda
// línea se dibujaba pisando el borde de la tarjeta.
function wrapLegendLines(doc, items, maxWidth) {
  doc.setFontSize(7);
  doc.setFont(undefined, "bold");
  // +16 (8pt por lado) en vez de +10 — con poco padding las pills se veían
  // muy apretadas entre sí y contra el texto.
  const pillWidths = items.map((item) => doc.getTextWidth(item.label) + 16);

  const lines = [];
  let currentLine = [];
  let currentW = 0;
  items.forEach((item, i) => {
    const pillW = pillWidths[i];
    const addedW = currentLine.length ? pillW + PILL_GAP : pillW;
    if (currentLine.length && currentW + addedW > maxWidth) {
      lines.push({ items: currentLine, width: currentW });
      currentLine = [];
      currentW = 0;
    }
    currentLine.push({ item, pillW });
    currentW += currentLine.length > 1 ? pillW + PILL_GAP : pillW;
  });
  if (currentLine.length) lines.push({ items: currentLine, width: currentW });
  return lines;
}

// Alto real que va a ocupar la leyenda al dibujarse — para reservarle
// espacio antes de escalar el gráfico, en vez de una altura fija adivinada.
function measureLegendHeight(doc, items, maxWidth) {
  if (!items || !items.length) return 0;
  const lines = wrapLegendLines(doc, items, maxWidth);
  return lines.length * PILL_H + (lines.length - 1) * PILL_GAP;
}

function drawLegendPills(doc, items, x, y, maxWidth) {
  doc.setFontSize(7);
  doc.setFont(undefined, "bold");
  const lines = wrapLegendLines(doc, items, maxWidth);

  // Centrar cada línea dentro del ancho disponible.
  lines.forEach((line, lineIdx) => {
    let cursorX = x + (maxWidth - line.width) / 2;
    const cursorY = y + lineIdx * (PILL_H + PILL_GAP);
    line.items.forEach(({ item, pillW }) => {
      doc.setFillColor(...hexToRgb(item.color));
      doc.roundedRect(cursorX, cursorY, pillW, PILL_H, PILL_H / 2, PILL_H / 2, "F");
      doc.setTextColor(255, 255, 255);
      doc.text(item.label, cursorX + pillW / 2, cursorY + PILL_H / 2 + 2.3, { align: "center" });
      cursorX += pillW + PILL_GAP;
    });
  });

  return lines.length * PILL_H + (lines.length - 1) * PILL_GAP; // alto total ocupado
}

// Dibuja una tarjeta de gráfico (fondo + título + gráfico + leyenda) en la
// posición y tamaño indicados.
export async function drawChartCard(doc, { x, y, width, height, title, svgEl, legendItems, isEmpty, singleSliceColor, naturalW, naturalH, emptyMessage = "Sin datos para el período" }) {
  // Gris, igual que el fondo del bloque resumen de KPIs en pantalla ($bg-resumen: #EDEBEB).
  doc.setFillColor(237, 235, 235);
  doc.roundedRect(x, y, width, height, 8, 8, "F");

  const pad = 10;
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.setTextColor(20, 40, 80);
  doc.text(title, x + pad, y + pad + 8);

  if (isEmpty || (!svgEl && !singleSliceColor)) {
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.setTextColor(150, 150, 150);
    doc.text(emptyMessage, x + width / 2, y + height / 2, { align: "center" });
    return;
  }

  const chartTop = y + pad + 22;
  const chartAvailW = width - pad * 2;
  // Medido según cuántas líneas va a necesitar la leyenda de verdad (puede
  // ser más de una con varios ítems de texto largo, ej. la barra "Efluentes
  // vs Consumo de agua") — antes era un alto fijo (26pt, una sola línea) y
  // una segunda línea terminaba dibujándose fuera de la tarjeta.
  const legendH = legendItems && legendItems.length
    ? measureLegendHeight(doc, legendItems, chartAvailW) + 8
    : 0;
  const chartAvailH = height - (chartTop - y) - pad - legendH;

  if (singleSliceColor) {
    // Círculo completo (100% en una sola categoría): se dibuja directo con
    // primitivas de jsPDF en vez del SVG de G2, que degenera en una espiral
    // para este caso (ver comentario en renderDonutForPdf). Siempre cuadrado.
    const chartSize = Math.max(40, Math.min(chartAvailW, chartAvailH));
    const chartX = x + (width - chartSize) / 2;
    const outerR = chartSize / 2;
    const innerR = outerR * 0.6; // mismo innerRadius que usa el config de G2
    const cx = chartX + outerR;
    const cy = chartTop + outerR;
    doc.setFillColor(...hexToRgb(singleSliceColor));
    doc.circle(cx, cy, outerR, "F");
    doc.setFillColor(237, 235, 235); // mismo color que el fondo de la tarjeta
    doc.circle(cx, cy, innerR, "F");

    if (legendItems && legendItems.length) {
      drawLegendPills(doc, legendItems, x + pad, chartTop + chartSize + 8, width - pad * 2);
    }
    return;
  }

  // Escala respetando la proporción real con la que se renderizó el SVG
  // (cuadrada para la dona, más ancha que alta para las barras) en vez de
  // forzar un cuadrado — forzarlo distorsionaba/recortaba las barras.
  const scale = Math.min(chartAvailW / naturalW, chartAvailH / naturalH);
  const chartW = naturalW * scale;
  const chartH = naturalH * scale;
  const chartX = x + (width - chartW) / 2;

  await doc.svg(svgEl, { x: chartX, y: chartTop, width: chartW, height: chartH });

  if (legendItems && legendItems.length) {
    drawLegendPills(doc, legendItems, x + pad, chartTop + chartH + 8, width - pad * 2);
  }
}

// Dibuja una sección de ancho completo SIN tarjeta gris: título + raya negra
// debajo (mismo estilo que el header de sección en pantalla — border-bottom
// negro de CollapsibleSection) y la leyenda de pills ARRIBA del gráfico
// (como la "CustomLegend" en pantalla), no abajo como en drawChartCard. Para
// los gráficos DualAxes de EnergyKPIsDashboard, que van a ancho completo en
// vez de en la grilla de 3 columnas.
export async function drawChartSection(doc, { x, y, width, height, title, svgEl, legendItems, isEmpty, emptyMessage = "Sin datos para el período" }) {
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.setTextColor(20, 40, 80);
  doc.text(title, x, y + 10);

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.6);
  doc.line(x, y + 16, x + width, y + 16);

  let cursorY = y + 16 + 12;

  if (isEmpty || !svgEl) {
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    doc.setTextColor(150, 150, 150);
    doc.text(emptyMessage, x + width / 2, y + height / 2, { align: "center" });
    return;
  }

  if (legendItems && legendItems.length) {
    const legendH = drawLegendPills(doc, legendItems, x, cursorY, width);
    cursorY += legendH + 10;
  }

  // A diferencia de drawChartCard (dona/barra angosta, donde hay que
  // preservar la proporción real para no achatar/estirar la forma), acá se
  // estira para llenar todo el ancho de la sección — un gráfico de serie
  // temporal ancho no tiene una "forma correcta" que distorsionar, y así
  // aprovecha el ancho completo de la hoja igual que en pantalla.
  const chartW = width;
  const chartH = y + height - cursorY;
  const chartX = x;

  await doc.svg(svgEl, { x: chartX, y: cursorY, width: chartW, height: chartH });
}

// Acomoda N tarjetas una al lado de la otra dentro del ancho disponible.
// `emptyMessage`: default para todas las tarjetas de la fila (una card
// puede pisarlo con su propio `emptyMessage` si hiciera falta).
export async function drawChartRow(doc, cards, { x, y, availW, cardH, gap = 12, emptyMessage }) {
  const cardW = (availW - gap * (cards.length - 1)) / cards.length;
  for (let i = 0; i < cards.length; i++) {
    await drawChartCard(doc, { emptyMessage, ...cards[i], x: x + i * (cardW + gap), y, width: cardW, height: cardH });
  }
}
